import { ampli } from '@/ampli';
import { format } from 'date-fns';
import { useEffect } from 'react';

import { TokenLaunchpadBadge } from '@/components/Badges/TokenLaunchpadBadge';

import { isLaunchpadSupported, launchpadDefinitions } from '@/constants/launchpads';

import type { TokenInfo } from '@/hooks/useTokenInfo';

import { fontStyles } from '@/styles/typography';

import { ClockIcon } from '@/ui/Icons/ClockIcon';
import { SmallDotIcon } from '@/ui/Icons/SmallDotIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { isTokenResultNativeAsset } from '@/utils/multichain';
import { normalizeTokenAddress, tokenKey } from '@/utils/normalizeAddress';
import { formatTimeElapsed } from '@/utils/numbers';
import { ZoraType } from '@/utils/zora';

import { LiquidityScore } from '../TokenLiquidityScore';
import { TokenProfileContractAddress } from '../TokenProfileContractAddress';
import { SecurityAuditPreview } from '../TokenProfileSecurityAudit/SecurityAuditPreview';

interface TokenSecondaryInfoProps {
  token: TokenResult;
  zoraType?: ZoraType;
  tokenInfo?: TokenInfo;
}

const reportedLaunchpads = new Set<string>();

export const TokenSecondaryInfo = ({ token, zoraType, tokenInfo }: TokenSecondaryInfoProps) => {
  const showTokenAddress = !isTokenResultNativeAsset(token);

  const creationTime = tokenInfo?.stats?.creationTime;
  const launchpadName = token.launchpad?.name ?? tokenInfo?.launchpad;

  // Report missing definitions and Token API attribution gaps once per token and launchpad.
  useEffect(() => {
    const codexLaunchpad = tokenInfo?.launchpad;
    const key = `${tokenKey(token.address, token.chainId)}:${codexLaunchpad}`;

    if (!codexLaunchpad || reportedLaunchpads.has(key)) return;

    const properties = {
      launchpad_id: codexLaunchpad,
      token_address: normalizeTokenAddress(token.address, token.chainId),
      token_chain_id: token.chainId,
      token_symbol: token.symbol,
    };

    if (!isLaunchpadSupported(codexLaunchpad)) {
      ampli.tokenLaunchpadDefinitionMissing(properties);
    } else if (!token.launchpad?.name && launchpadDefinitions[codexLaunchpad].tokenApiSupport) {
      ampli.tokenLaunchpadMissingFromTokenApi(properties);
    } else {
      return;
    }

    reportedLaunchpads.add(key);
  }, [tokenInfo?.launchpad, token.address, token.chainId, token.launchpad?.name, token.symbol]);

  return (
    <div
      className={fontStyles({
        variant: 'BODY_MD',
        class: 'flex flex-wrap items-center gap-2 flex-row w-full ml-1 text-token-gray-800',
      })}
    >
      <span className="hidden tablet:inline">{token.chainName}</span>
      {showTokenAddress && (
        <>
          <SmallDotIcon className="hidden tablet:inline text-token-gray-500" />
          <TokenProfileContractAddress token={token} className="-ml-1" />
        </>
      )}
      <SmallDotIcon
        className={
          showTokenAddress
            ? '-ml-1 text-token-gray-500'
            : 'hidden tablet:inline -ml-1 text-token-gray-500'
        }
      />
      {creationTime != null && (
        <>
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <button type="button" className="flex flex-row items-center gap-1 cursor-default">
                <ClockIcon className="text-token-gray-700" />
                {formatTimeElapsed(creationTime, true)}
              </button>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>
              {format(new Date(creationTime * 1000), 'Pp')}
            </MatchaTooltipContent>
            <MatchaTooltipPopover sideOffset={8} />
            <MatchaTooltipBottomSheet header="Token creation time" />
          </MatchaTooltip>
          <SmallDotIcon className="hidden text-token-gray-500 tablet:inline" />
        </>
      )}
      <div className="flex min-w-0 max-w-full basis-full flex-row gap-1 tablet:basis-auto">
        <SecurityAuditPreview activeToken={token} />
        <LiquidityScore activeToken={token} zoraType={zoraType} />
        {launchpadName && <TokenLaunchpadBadge id={launchpadName} />}
      </div>
    </div>
  );
};
